// Drawing subtitles on a picture: the same drawing is used for the preview and for the video that is saved, so what you see is what you get.
import { cueAt, cueText, wrapLines } from "./cues";

export const PRESETS = {
  classic: { label: "Classic", color: "#ffffff", box: "rgba(0,0,0,0.7)", outline: 0, weight: 600, radius: 0.22 },
  bold: { label: "Bold yellow", color: "#ffe14d", box: null, outline: 0.14, outlineColor: "#000000", weight: 800, radius: 0 },
  pill: { label: "Pill", color: "#ffffff", box: "rgba(18,22,40,0.85)", outline: 0, weight: 600, radius: 0.6 },
  clean: { label: "Clean", color: "#ffffff", box: null, outline: 0.06, outlineColor: "rgba(0,0,0,0.9)", weight: 700, radius: 0, shadow: true },
  highlight: { label: "Highlight", color: "#101418", box: "#ffe14d", outline: 0, weight: 700, radius: 0.12 },
};
export const PRESET_IDS = Object.keys(PRESETS);
export const POSITIONS = { bottom: "Bottom", middle: "Middle", top: "Top" };
export const DEFAULT_STYLE = { preset: "classic", size: 100, position: "bottom", color: "", font: "sans" };
export const FONTS = {
  sans: { label: "Clean", family: '"Inter", "Segoe UI", Roboto, Arial, "Noto Naskh Arabic", "Noto Nastaliq Urdu", sans-serif' },
  serif: { label: "Serif", family: 'Georgia, "Times New Roman", "Noto Naskh Arabic", serif' },
  mono: { label: "Typewriter", family: '"Courier New", Consolas, monospace' },
};

export const isRtl = (text) => /[֐-ࣿיִ-﷿ﹰ-ﻼ]/.test(text);

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(x, y, w, h, r);
  else ctx.rect(x, y, w, h);
}

// The size of the letters for a picture of this size (a tall picture is narrow: it goes by the width).
export const baseSize = (W, H) => (H > W ? W * 0.062 : H * 0.052);

// Draws `text` (lines separated by \n) on the picture: `style` = { preset, size (percent), position, color, font }. Lines too wide for the picture are made smaller to fit.
export function drawCaption(ctx, W, H, text, style = DEFAULT_STYLE) {
  const preset = PRESETS[style.preset] || PRESETS.classic;
  const family = (FONTS[style.font] || FONTS.sans).family;
  const lines = String(text).split("\n").filter((l) => l.trim());
  if (!lines.length) return;
  let size = baseSize(W, H) * ((style.size || 100) / 100);
  const maxWidth = W * (H > W ? 0.92 : 0.86);
  ctx.save();
  const font = (px) => `${preset.weight} ${px}px ${family}`;
  ctx.font = font(size);
  const widest = Math.max(...lines.map((l) => ctx.measureText(l).width));
  if (widest > maxWidth) size *= maxWidth / widest;
  ctx.font = font(size);
  const lineHeight = size * 1.32;
  const padX = size * 0.5;
  const padY = size * 0.14;
  const blockHeight = lines.length * lineHeight;
  const margin = H * 0.06;
  const top = style.position === "top" ? margin : style.position === "middle" ? (H - blockHeight) / 2 : H - margin - blockHeight;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  lines.forEach((line, i) => {
    const y = top + lineHeight * (i + 0.5);
    ctx.direction = isRtl(line) ? "rtl" : "ltr";
    const width = ctx.measureText(line).width;
    if (preset.box) {
      ctx.fillStyle = preset.box;
      roundRect(ctx, W / 2 - width / 2 - padX, y - lineHeight / 2 + padY, width + padX * 2, lineHeight - padY * 2, size * preset.radius);
      ctx.fill();
    }
    if (preset.outline) {
      ctx.lineJoin = "round";
      ctx.lineWidth = size * preset.outline * 2;
      ctx.strokeStyle = preset.outlineColor;
      ctx.strokeText(line, W / 2, y);
    }
    if (preset.shadow) { ctx.shadowColor = "rgba(0,0,0,0.6)"; ctx.shadowBlur = size * 0.2; ctx.shadowOffsetY = size * 0.05; }
    ctx.fillStyle = style.color || preset.color;
    ctx.fillText(line, W / 2, y);
    ctx.shadowColor = "transparent";
  });
  ctx.restore();
}

// The text shown for a cue: its lines, broken as the options say (`which`: "text" | "alt" | "both").
export function lineText(cue, which, { maxChars, maxLines } = {}) {
  return cueText(cue, which, { maxChars, maxLines });
}

// A picture engine for the video processor (see upscale/process.js): draws every frame of the video with its subtitle on a 2D canvas.
// look = { cues, style, which, maxChars, maxLines }
export class CaptionEngine {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.size = [0, 0];
  }

  setOutput(w, h) {
    this.size = [w, h];
    if (this.canvas.width !== w || this.canvas.height !== h) { this.canvas.width = w; this.canvas.height = h; }
  }

  render(video, look) {
    const [w, h] = this.size;
    this.ctx.drawImage(video, 0, 0, w, h);
    const cue = cueAt(look.cues, video.currentTime);
    if (cue) drawCaption(this.ctx, w, h, lineText(cue, look.which, look), look.style);
  }

  dispose() {}
}

export { wrapLines };
